import { chain } from "lodash";

import {
  type ChartableResponseRowWithChildren,
  descriptorToPath,
  pathToDescriptor,
} from "@north-beam/nb-client-side";
import {
  type Breakdown,
  type CategoricalBreakdownConfig,
  type DbForecastV1DateRow,
  type SalesReportChartRow,
  metrics,
} from "@north-beam/nb-common";

export const preprocessForChart = (
  rows: SalesReportChartRow[],
  breakdownConfigs: CategoricalBreakdownConfig[],
  breakdowns: Breakdown[],
): ChartableResponseRowWithChildren => {
  function generateDescriptors(dimensions: Record<string, string>): string[] {
    const paths: string[][] = [[]];
    for (const breakdown of breakdowns) {
      const dimension = dimensions[breakdown.key] || "";
      paths.push([...paths[paths.length - 1], dimension]);
    }

    return paths.map(pathToDescriptor);
  }

  const keyToConfig = chain(breakdownConfigs)
    .groupBy("key")
    .mapValues((v) => v[0])
    .value();

  const allAggregationPaths: string[][] = [[]];
  for (const breakdown of breakdowns) {
    const currentPaths = [...allAggregationPaths];
    const newPaths: string[][] = [];
    for (const value of breakdown.values) {
      for (const descriptor of currentPaths) {
        newPaths.push([...descriptor, value]);
      }
    }

    allAggregationPaths.push(...newPaths);
  }
  const allAggregationDescriptors = allAggregationPaths.map(pathToDescriptor);

  const descriptorToData: Record<string, metrics.MetricSeries> = chain(
    allAggregationDescriptors,
  )
    .map((v) => [v, {}])
    .fromPairs()
    .value();

  for (const row of rows) {
    const { dimensions, dailyMetrics, forecastV1Daily } = row;
    if (!dailyMetrics) {
      continue;
    }

    const dateToForecastRow: Record<string, DbForecastV1DateRow> = {};
    if (forecastV1Daily) {
      for (const forecastRow of forecastV1Daily) {
        dateToForecastRow[forecastRow.date] = forecastRow;
      }
    }

    const descriptors = generateDescriptors(dimensions);

    const rowPassesFilter = descriptors.every((d) => !!descriptorToData[d]);

    if (!rowPassesFilter) {
      continue;
    }

    for (const dailyMetricPoint of dailyMetrics) {
      const metricPoint = metrics.makeMetricPoint(
        dailyMetricPoint,
        dateToForecastRow[dailyMetricPoint.date],
      );
      for (const descriptor of descriptors) {
        const data = descriptorToData[descriptor];
        metrics.addToMetricSeries(data, dailyMetricPoint.date, metricPoint);
      }
    }
  }

  const descriptorToRow: Record<string, ChartableResponseRowWithChildren> =
    chain(descriptorToData)
      .pickBy((value) => Object.keys(value).length > 0)
      .mapValues((value, key) => {
        const path = descriptorToPath(key);
        const description: { key: string; value: string }[] = [];
        for (let i = 0; i < path.length; ++i) {
          const breakdownKey = breakdowns[i].key;
          const choices = keyToConfig[breakdownKey]?.choices || [];
          const name =
            choices.find((v) => v.value === path[i])?.label ?? path[i];
          description.push({ key: breakdownKey, value: name });
        }
        return {
          description,
          series: value!,
          children: [],
        };
      })
      .value();

  for (const descriptor in descriptorToRow) {
    const path = descriptorToPath(descriptor);
    if (path.length === 0) {
      continue;
    }

    const parentPath = [...path];
    parentPath.pop();

    const parentDescriptor = pathToDescriptor(parentPath);
    descriptorToRow[parentDescriptor].children.push(
      descriptorToRow[descriptor],
    );
  }

  return descriptorToRow[pathToDescriptor([])];
};
